'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { setGlobalToastAdd } from '../utils/toast';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export type Toast = {
  id: string;
  message: string;
  description?: string;
  type: ToastType;
  duration?: number;
  refreshAction?: {
    label: string;
    onRefresh: () => void;
  };
};

type ToastContextType = {
  toasts: Toast[];
  addToast: (
    message: string,
    description?: string,
    type?: ToastType,
    duration?: number,
    refreshAction?: { label: string; onRefresh: () => void }
  ) => void;
  removeToast: (id: string) => void;
  showSuccess: (message: string, description?: string, duration?: number) => void;
  showError: (message: string, description?: string, duration?: number) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    (
      message: string,
      description?: string,
      type: ToastType = 'info',
      duration = 3000,
      refreshAction?: { label: string; onRefresh: () => void }
    ) => {
      const id = Date.now().toString();
      const newToast: Toast = {
        id,
        message,
        type,
        duration,
        ...(description !== undefined && { description }),
        ...(refreshAction !== undefined && { refreshAction }),
      };

      setToasts(prev => [...prev, newToast]);

      setTimeout(() => {
        removeToast(id);
      }, duration);
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showSuccess = useCallback(
    (message: string, description?: string, duration = 3000) => {
      addToast(message, description, 'success', duration);
    },
    [addToast]
  );

  const showError = useCallback(
    (message: string, description?: string, duration = 5000) => {
      addToast(message, description, 'error', duration);
    },
    [addToast]
  );

  // Connect the global toast utility to this provider
  useEffect(() => {
    setGlobalToastAdd(addToast);
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, showSuccess, showError }}>
      {children}
    </ToastContext.Provider>
  );
};
